"use client";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icons } from "@/components/Icons";
import { Badge, Button, Card, Stepper } from "@/components/ui";
import { FoodImg } from "@/components/FoodImg";
import type { ModifierGroup, ModifierOption } from "@/types/menu";
import { useCart } from "@/store/cart";
import type { CartLine } from "@/store/cart";
import { cartLineToOrderLineInput } from "@/lib/order-line-payload";
import { useShortlist } from "@/store/shortlist";
import { useAppMode } from "@/hooks/useAppMode";
import { useGroupCart } from "@/store/group-cart";
import { useBranch } from "@/store/branch";
import { useBranchOpen } from "@/hooks/useBranchOpen";
import {
  useMenu,
  mapModifierGroups,
  mapVariantGroups,
  type UiMenuItem,
  type UiVariantGroup,
} from "@/hooks/useMenu";
import { useFormatPrice } from "@/hooks/useFormatPrice";
import { Price } from "@/components/Price";
import { track } from "@/lib/analytics";
import type { NutritionalInformationObj } from "@/api/catalog-service/model";
import type {
  SelectedModifierOption,
  SelectedVariantOption,
  OrderLineInput,
} from "@/api/online-ordering-service/model";
import { orderApi } from "@/api/services";
import { computeEffectiveBasePrice } from "./itemPricing";
import { useItemReviews } from "@/hooks/useItemReviews";
import { PublicReviewCard } from "@/components/reviews/PublicReviewCard";
import { StarRating } from "@/components/reviews/StarRating";

type Nutri = {
  calories: number;
  sodium: number;
  sodiumHigh: boolean;
  caffeine: number;
  walkingMin: number;
  fat: number;
  sugar: number;
  protein: number;
};

function emptyNutrition(): Nutri {
  return {
    calories: 0,
    sodium: 0,
    sodiumHigh: false,
    caffeine: 0,
    walkingMin: 0,
    fat: 0,
    sugar: 0,
    protein: 0,
  };
}

function hasNutritionData(n: Nutri): boolean {
  return (
    n.calories > 0 ||
    n.sodium > 0 ||
    n.sodiumHigh ||
    n.caffeine > 0 ||
    n.fat > 0 ||
    n.sugar > 0 ||
    n.protein > 0
  );
}

function mapNutrition(
  api: NutritionalInformationObj | undefined | null,
): Nutri {
  if (!api) return emptyNutrition();
  const macros = api.macronutrients ?? null;
  return {
    calories: typeof api.calories === "number" ? api.calories : 0,
    sodium: typeof api.sodiumMg === "number" ? api.sodiumMg : 0,
    sodiumHigh: !!api.highSodiumWarning,
    caffeine: typeof api.caffeineMg === "number" ? api.caffeineMg : 0,
    // Mirrors v1's estimate: Minutes of walking = Calories * 0.25.
    walkingMin:
      typeof api.calories === "number" ? Math.round(api.calories * 0.25) : 0,
    fat:
      typeof macros?.totalFatG === "number" ? macros.totalFatG : 0,
    sugar:
      typeof macros?.sugarsG === "number" ? macros.sugarsG : 0,
    protein:
      typeof macros?.proteinG === "number" ? macros.proteinG : 0,
  };
}

function addNutrition(target: Nutri, source: NutritionalInformationObj | null | undefined, qty = 1): boolean {
  const n = mapNutrition(source);
  if (!hasNutritionData(n)) return false;
  target.calories += n.calories * qty;
  target.sodium += n.sodium * qty;
  target.caffeine += n.caffeine * qty;
  target.walkingMin += n.walkingMin * qty;
  target.fat += n.fat * qty;
  target.sugar += n.sugar * qty;
  target.protein += n.protein * qty;
  target.sodiumHigh ||= n.sodiumHigh;
  return true;
}

function defaultSingleOption(g: ModifierGroup): string | null {
  const explicit =
    g.options.find((o) => o.isDefaultSelection) ??
    g.options.find((o) => (o.defaultQuantity ?? 0) > 0);
  if (explicit) return explicit.id;
  return null;
}

function defaultMultiOptions(g: ModifierGroup): Record<string, number> {
  const next: Record<string, number> = {};
  const max = g.max ?? Infinity;
  let total = 0;
  for (const opt of g.options) {
    const rawQty = opt.defaultQuantity ?? (opt.isDefaultSelection ? 1 : 0);
    const optMax = opt.maxQuantity ?? g.optionMax ?? (g.max ?? Infinity);
    const qty = Math.max(0, Math.min(rawQty, optMax, max - total));
    if (qty > 0) {
      next[opt.id] = qty;
      total += qty;
    }
    if (total >= max) break;
  }

  return next;
}

const floatBtn: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 999,
  background: "rgba(255,255,255,0.92)",
  backdropFilter: "blur(8px)",
  border: "1px solid rgba(0,0,0,0.05)",
  display: "grid",
  placeItems: "center",
  color: "var(--ink-1)",
  cursor: "pointer",
  boxShadow: "0 4px 12px -4px rgba(0,0,0,0.18)",
};

const qtyBtn: React.CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: 999,
  background: "transparent",
  border: "none",
  color: "var(--primary-700)",
  fontSize: 16,
  fontWeight: 700,
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
  fontFamily: "inherit",
};

type SelState = Record<string, string | Record<string, number> | null>;

export function ItemDetailScreen() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editCartLineId = searchParams.get("editCartLineId");
  const editShortlistLineId = searchParams.get("editShortlistLineId");
  // Recommendation attribution (FR-019 Phase 1) — present when this screen
  // was deep-linked from a rec strip tap (item needed customization). Carried
  // onto the cart line / append-line payload so the order-create request can
  // attribute the conversion.
  const recId = searchParams.get("recId");
  const recAttribution = recId
    ? {
        recommendationId: recId,
        recommendationSource: searchParams.get("recSource"),
        recommendationSurface: "online",
        recommendationReasonCode: searchParams.get("recReasonCode"),
        recommendationRank: searchParams.get("recRank") != null && searchParams.get("recRank") !== ""
          ? Number(searchParams.get("recRank"))
          : null,
      }
    : null;
  const { cart, addToCart, updateLine, appendOrderId, setAppendOrderId } = useCart();
  const { isMenuOnly } = useAppMode();
  const { shortlist, addToShortlist, updateLine: updateShortlistLine } = useShortlist();
  const editLine = useMemo(() => {
    if (editShortlistLineId) {
      return shortlist.find((l) => l.id === editShortlistLineId) ?? null;
    }
    if (!editCartLineId) return null;
    return cart.find((l) => l.id === editCartLineId) ?? null;
  }, [cart, shortlist, editCartLineId, editShortlistLineId]);
  // const { addToCart } = useCart();
  const groupCart = useGroupCart();
  const { requiresBranchSelection } = useBranch();
  const { isClosed, opensAt } = useBranchOpen();
  const { i18n, t } = useTranslation();
  const lang = i18n.language || "en";
  const { items, loading, error, branchBlocks } = useMenu();
  const { format } = useFormatPrice();
  const item = useMemo<UiMenuItem | undefined>(
    () => items.find((m) => m.id === itemId),
    [items, itemId],
  );
  const itemReviews = useItemReviews(item?.id);
  const relatedItems = useMemo<UiMenuItem[]>(() => {
    if (!item) return [];
    const out: UiMenuItem[] = [];
    const seen = new Set<string>([item.id]);
    const push = (candidate: UiMenuItem) => {
      if (seen.has(candidate.id) || !candidate.isAvailable) return;
      seen.add(candidate.id);
      out.push(candidate);
    };
    items.filter((candidate) => candidate.cat === item.cat).forEach(push);
    items.filter((candidate) => candidate.popular).forEach(push);
    items.forEach(push);
    return out.slice(0, 8);
  }, [item, items]);

  useLayoutEffect(() => {
    const scroller = document.querySelector<HTMLElement>(".phone-content");
    scroller?.scrollTo({ top: 0, behavior: "auto" });
  }, [itemId]);

  // Fire `item_viewed` once per item — when it resolves, not on every render.
  useEffect(() => {
    if (!item) return;
    track("item_viewed", {
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      category: item.cat,
    });
  }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Modifier groups + variants come from the rendered menu payload. Groups can
  // be scoped to specific variant options, so the visible set is derived after
  // variant defaults are known.
  const allGroups = useMemo<ModifierGroup[]>(
    () =>
      mapModifierGroups(
        item?.raw?.modifierGroups,
        lang,
        branchBlocks.blockedItemIds,
      ),
    [item, lang, branchBlocks.blockedItemIds],
  );
  const variantGroups = useMemo<UiVariantGroup[]>(
    () =>
      mapVariantGroups(
        item?.raw?.variantGroups,
        lang,
        branchBlocks.blockedVariants,
      ),
    [item, lang, branchBlocks.blockedVariants],
  );

  const [variantSel, setVariantSel] = useState<Record<string, string>>({});
  const variantGroupsKey = variantGroups
    .map((g) => `${g.id}:${g.options.map((o) => `${o.id}:${o.price}`).join(",")}`)
    .join("|");
  useEffect(() => {
    const init: Record<string, string> = {};
    if (editLine) {
      editLine.selectedVariants.forEach((v) => {
        init[v.variantGroupId] = v.variantOptionId;
      });
    } else {
      variantGroups.forEach((g) => {
      });
    }
    setVariantSel(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantGroupsKey, editLine]);

  const selectedVariantOptionIds = useMemo(
    () => new Set(Object.values(variantSel).filter(Boolean)),
    [variantSel],
  );

  const groups = useMemo<ModifierGroup[]>(
    () =>
      allGroups.filter((g) => {
        const allowed = g.restrictedToVariantOptionIds ?? [];
        return allowed.length === 0 || allowed.some((id) => selectedVariantOptionIds.has(id));
      }),
    [allGroups, selectedVariantOptionIds],
  );

  const [sel, setSel] = useState<SelState>({});
  // (Re)initialise selection state whenever the resolved groups change.
  // This handles the case where groups are loaded async (item.raw arrives
  // after first paint).
  const groupsKey = groups
    .map((g) =>
      `${g.id}:${g.options
        .map((o) => `${o.id}:${o.defaultQuantity ?? ""}:${o.isDefaultSelection ? 1 : 0}:${o.maxQuantity ?? ""}`)
        .join(",")}`,
    )
    .join("|");
  useEffect(() => {
    const s: SelState = {};
    if (editLine) {
      groups.forEach((g) => {
        if (g.multi) {
          const modSelections: Record<string, number> = {};
          editLine.selectedModifiers
            .filter((m) => m.modifierGroupId === g.id)
            .forEach((m) => {
              modSelections[m.modifierOptionId] = m.quantity;
            });
          s[g.id] = modSelections;
        } else {
          const m = editLine.selectedModifiers.find((m) => m.modifierGroupId === g.id);
          s[g.id] = m?.modifierOptionId ?? defaultSingleOption(g);
        }
      });
    } else {
      groups.forEach((g) => {
        if (g.multi) s[g.id] = defaultMultiOptions(g);
        else s[g.id] = defaultSingleOption(g);
      });
    }
    setSel(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsKey, editLine]);
  const nutri = useMemo<Nutri>(() => {
    if (!item) return emptyNutrition();
    const selected = emptyNutrition();
    let hasSelectedNutrition = false;

    for (const vg of variantGroups) {
      const selId = variantSel[vg.id];
      const opt = vg.options.find((o) => o.id === selId);
      if (opt) {
        hasSelectedNutrition =
          addNutrition(selected, opt.nutritionalInformation) || hasSelectedNutrition;
      }
    }

    for (const g of groups) {
      const v = sel[g.id];
      if (g.multi) {
        Object.entries((v as Record<string, number>) || {}).forEach(([oid, q]) => {
          const opt = g.options.find((o) => o.id === oid);
          if (!opt || q <= 0) return;
          hasSelectedNutrition =
            addNutrition(selected, opt.nutritionalInformation, q) || hasSelectedNutrition;
        });
      } else if (typeof v === "string" && v) {
        const opt = g.options.find((o) => o.id === v);
        if (opt) {
          hasSelectedNutrition =
            addNutrition(selected, opt.nutritionalInformation) || hasSelectedNutrition;
        }
      }
    }

    return hasSelectedNutrition ? selected : mapNutrition(item.raw?.nutritionalInformation);
  }, [groups, item, sel, variantGroups, variantSel]);
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  useEffect(() => {
    if (editLine) {
      setQty(editLine.qty);
      setNotes(editLine.notes);
    }
  }, [editLine]);
  const [showNotes, setShowNotes] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [placing, setPlacing] = useState(false);
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // IMPORTANT: this useMemo must stay above the early returns below.
  // React requires hook call order to be stable across renders — moving
  // this past the conditional `return`s makes hooks #56+ disappear on the
  // first render (when `loading && !item` short-circuits) and reappear on
  // the second, which is the "change in the order of Hooks" warning.
  const effectiveBasePrice = useMemo(() => {
    if (!item) return 0;
    // Variant option prices are *adjustments* added to the item base price.
    // Previously this returned the variant adjustment *instead of* the base
    // whenever a variant was selected, dropping the item's base price.
    return computeEffectiveBasePrice(item.price, variantGroups, variantSel);
  }, [item, variantGroups, variantSel]);

  const extras = useMemo(() => {
    if (!item) return 0;
    let total = 0;
    groups.forEach((g) => {
      const v = sel[g.id];
      if (g.multi) {
        Object.entries((v as Record<string, number>) || {}).forEach(([oid, q]) => {
          const opt = g.options.find((o) => o.id === oid);
          if (opt) total += opt.price * q;
        });
      } else if (typeof v === "string" && v) {
        total += g.options.find((o) => o.id === v)?.price || 0;
      }
    });
    return total;
  }, [sel, groups, item]);

  // Digital-menu links have no location step: browse items against the
  // preview branch instead of bouncing to the (blocked) map picker.
  if (requiresBranchSelection && !isMenuOnly) {
    return (
      <ItemBranchSelectionRequired
        onBack={() => navigate(-1)}
        onSelectLocation={() => navigate("/specify-location?returnTo=home")}
      />
    );
  }
  if (loading && !item) return <ItemDetailSkeleton />;
  if (error && !item)
    return (
      <ItemDetailError
        onBack={() => navigate(-1)}
        onRetry={() => window.location.reload()}
      />
    );
  if (!item) return <div style={{ padding: 32 }}>{t("v2.item.item_not_found")}</div>;

  const multiCount = (gid: string) => {
    const m = (sel[gid] as Record<string, number> | null) || {};
    return Object.values(m).reduce((n, q) => n + q, 0);
  };
  const optQty = (gid: string, oid: string) =>
    ((sel[gid] as Record<string, number> | null) || {})[oid] || 0;

  const optionMax = (g: ModifierGroup, opt: ModifierOption | undefined) =>
    opt?.maxQuantity ?? g.optionMax ?? (g.multi ? (g.max ?? Infinity) : Math.max(opt?.defaultQuantity ?? 0, 1));

  const setOptQty = (g: ModifierGroup, optId: string, next: number) => {
    const cur = { ...((sel[g.id] as Record<string, number>) || {}) };
    const opt = g.options.find((o) => o.id === optId);
    const otherCount = Object.entries(cur).reduce(
      (sum, [id, count]) => (id === optId ? sum : sum + count),
      0,
    );
    const capped = Math.min(
      Math.max(0, next),
      optionMax(g, opt),
      (g.max ?? Infinity) - otherCount,
    );
    if (capped <= 0) delete cur[optId];
    else cur[optId] = capped;
    setSel({ ...sel, [g.id]: cur });
  };

  const incOpt = (g: ModifierGroup, optId: string) => {
    const cap = g.max ?? Infinity;
    const opt = g.options.find((o) => o.id === optId);
    const oCap = optionMax(g, opt);
    if (multiCount(g.id) >= cap && !optQty(g.id, optId)) return;
    const cur = optQty(g.id, optId);
    if (cur >= oCap) return;
    if (multiCount(g.id) + 1 > cap) return;
    setOptQty(g, optId, cur + 1);
  };
  const decOpt = (g: ModifierGroup, optId: string) =>
    setOptQty(g, optId, Math.max(0, optQty(g.id, optId) - 1));
  const toggleMulti = (g: ModifierGroup, optId: string) => {
    const opt = g.options.find((o) => o.id === optId);
    if (optionMax(g, opt) > 1) return;
    const cur = optQty(g.id, optId);
    if (cur > 0) setOptQty(g, optId, 0);
    else {
      const cap = g.max ?? Infinity;
      if (multiCount(g.id) >= cap) return;
      setOptQty(g, optId, 1);
    }
  };

  const groupValid = (g: ModifierGroup): boolean => {
    if (g.multi) {
      const c = multiCount(g.id);
      const min = g.min ?? (g.required ? 1 : 0);
      const max = g.max ?? Infinity;
      return c >= min && c <= max;
    }
    if (g.required) return !!sel[g.id];
    return true;
  };
  const invalidVariantGroups = variantGroups.filter((vg) => !variantSel[vg.id]);
  const invalidGroups = groups.filter((g) => !groupValid(g));
  const isValid = invalidGroups.length === 0 && invalidVariantGroups.length === 0;

  const lineTotal = (effectiveBasePrice + extras) * qty;

  const soldOut = !item.isAvailable;
  // Opening hours gate ordering, not browsing: a closed branch must not stop
  // someone shortlisting from the menu on their way over.
  const orderingDisabled = soldOut || (isClosed && !isMenuOnly);

  const handleAdd = () => {
    if (orderingDisabled) return;
    if (!isValid) {
      setAttempted(true);
      const firstId = invalidVariantGroups[0]?.id ?? invalidGroups[0]?.id;
      if (!firstId) return;
      const el = groupRefs.current[firstId];
      if (el) {
        const scroller = el.closest(".phone-content") as HTMLElement | null;
        if (scroller) {
          const top =
            el.getBoundingClientRect().top -
            scroller.getBoundingClientRect().top +
            scroller.scrollTop -
            80;
          scroller.scrollTo({ top, behavior: "smooth" });
        }
      }
      return;
    }
    const apiGroups = item.raw?.modifierGroups ?? [];
    const selectedModifiers: SelectedModifierOption[] = [];
    const modsLabel: string[] = [];
    groups.forEach((g) => {
      const apiGroup = apiGroups.find((ag) => ag.id === g.id);
      const v = sel[g.id];
      if (g.multi) {
        Object.entries((v as Record<string, number>) || {}).forEach(([oid, q]) => {
          const opt = g.options.find((x) => x.id === oid);
          if (!opt || q <= 0) return;
          const apiOpt = apiGroup?.options?.find((o) => o.id === oid);
          selectedModifiers.push({
            modifierGroupId: g.id,
            modifierGroupName: apiGroup?.name ?? null,
            modifierOptionId: oid,
            modifierOptionName: apiOpt?.name ?? null,
            quantity: q,
            unitPrice: opt.price,
            imageUrl: opt.imageUrl ?? apiOpt?.imageUrl ?? null,
          });
          modsLabel.push(q > 1 ? `${q}× ${opt.label}` : opt.label);
        });
      } else if (typeof v === "string" && v) {
        const opt = g.options.find((o) => o.id === v);
        if (!opt) return;
        const apiOpt = apiGroup?.options?.find((o) => o.id === v);
        selectedModifiers.push({
          modifierGroupId: g.id,
          modifierGroupName: apiGroup?.name ?? null,
          modifierOptionId: v,
          modifierOptionName: apiOpt?.name ?? null,
          quantity: 1,
          unitPrice: opt.price,
          imageUrl: opt.imageUrl ?? apiOpt?.imageUrl ?? null,
        });
        if (v !== "none" && v !== "regular") modsLabel.push(opt.label);
      }
    });

    const selectedVariants: SelectedVariantOption[] = [];
    variantGroups.forEach((g) => {
      const selId = variantSel[g.id];
      if (!selId) return;
      const opt = g.options.find((o) => o.id === selId);
      if (!opt) return;
      selectedVariants.push({
        variantGroupId: g.id,
        variantGroupName: g.name,
        variantOptionId: opt.id,
        variantOptionName: opt.name,
        priceAdjustment: opt.price,
        imageUrl: opt.imageUrl ?? null,
      });
      modsLabel.unshift(opt.label);
    });

    // const newLine = {
    //   id: editLine ? editLine.id : `${item.id}-${Date.now()}`,
    // Group-cart branch: when an active group is in play, the line lives on
    // the server so every guest at the table sees it immediately. We skip the
    // local cart and let the SSE snapshot/event update everyone's state.
    if (groupCart.status === "active" && groupCart.cart) {
      void groupCart
        .addLine({
          catalogItemId: item.id,
          quantity: qty,
          variantSelections: selectedVariants,
          modifierSelections: selectedModifiers,
          notes: notes.trim() || null,
        })
        .then(() => {
          navigate("/cart");
        })
        .catch((err) => {
          console.warn("[item] group addLine failed", err);
        });
      return;
    }

    const baseAmount = effectiveBasePrice + extras;

    if (appendOrderId) {
      // Build through the shared builder so the append payload can never drift
      // from the create payload — in particular the pricing decomposition
      // (lib/order-line-pricing-contract/SPEC.md).
      const linePayload: OrderLineInput = {
        ...cartLineToOrderLineInput(
          {
            id: `${item.id}-append`,
            itemId: item.id,
            name: item.name,
            categoryId: item.cat,
            categoryName: item.categoryName,
            categoryNameRaw: item.categoryNameRaw,
            price: baseAmount,
            basePrice: item.price,
            qty,
            tone: item.tone,
            imageUrl: item.imageUrl,
            selectedModifiers,
            selectedVariants,
            modsLabel,
            notes: notes.trim(),
          } as unknown as CartLine,
          1,
        ),
        // Rec attribution (FR-019) — dine-in append path sends the line
        // straight to the backend, so attach it here directly.
        ...(recAttribution ?? {}),
      };

      setPlacing(true);
      orderApi.addLine(appendOrderId, linePayload).then(() => {
        setAppendOrderId(null);
        navigate(`/dine-in-order/${appendOrderId}`);
      }).catch((err) => {
        console.error("Failed to append order line", err);
        alert(typeof err.message === "string" ? err.message : "Failed to add item to existing order. Creating a new order instead.");
      }).finally(() => {
        setPlacing(false);
      });
      return;
    }

    const newLine = {
      id: editLine ? editLine.id : `${item.id}-${Date.now()}`,
      itemId: item.id,
      name: item.name,
      categoryId: item.cat,
      categoryName: item.categoryName,
      categoryNameRaw: item.categoryNameRaw,
      price: effectiveBasePrice + extras,
      basePrice: item.price,
      qty,
      tone: item.tone,
      imageUrl: item.imageUrl,
      selectedModifiers,
      selectedVariants,
      modsLabel,
      notes: notes.trim(),
      // Rec attribution (FR-019): fresh adds deep-linked from a rec strip
      // carry the query-string attribution; edits preserve whatever the
      // existing line already carried (newLine is rebuilt from scratch).
      ...(editLine
        ? {
            recommendationId: (editLine as any).recommendationId,
            recommendationSource: (editLine as any).recommendationSource,
            recommendationSurface: (editLine as any).recommendationSurface,
            recommendationReasonCode: (editLine as any).recommendationReasonCode,
            recommendationRank: (editLine as any).recommendationRank,
          }
        : recAttribution ?? {}),
    };

    if (isMenuOnly) {
      // Same selections, different destination: a shortlist line carries no
      // recommendation attribution because there is no order to attribute.
      const {
        recommendationId: _rid,
        recommendationSource: _rsrc,
        recommendationSurface: _rsurf,
        recommendationReasonCode: _rcode,
        recommendationRank: _rrank,
        ...shortlistLine
      } = newLine;
      
      if (editShortlistLineId) {
        updateShortlistLine(editShortlistLineId, shortlistLine);
        navigate("/shortlist");
      } else {
        addToShortlist(shortlistLine);
        navigate("/");
      }
      return;
    }

    if (editLine) {
      updateLine(editLine.id, newLine);
      navigate("/cart");
    } else {
      addToCart(newLine);
      navigate("/");
    }
  };

  return (
    <div style={{ paddingBottom: 0, position: "relative" }}>
      <div style={{ position: "relative" }}>
        <FoodImg
          tone={item.tone}
          rounded={0}
          label={item.id}
          src={item.imageUrl}
          alt={item.name}
          thumbnailSize="400x400"
          style={{ aspectRatio: "1 / 0.85" }}
        />
        <div
          style={{
            position: "absolute",
            top: 14,
            left: 14,
            right: 14,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <button onClick={() => navigate(-1)} aria-label={t("v2.common.back")} style={floatBtn}>
            <Icons.back size={18} />
          </button>
          {/* <div style={{ display: "flex", gap: 8 }}>
            <button aria-label="Favorite" style={floatBtn}>
              <Icons.heart size={18} />
            </button>
            <button aria-label="Info" style={floatBtn}>
              <Icons.info size={18} />
            </button>
          </div> */}
        </div>
      </div>

      <div style={{ padding: "18px 16px 24px", display: "flex", flexDirection: "column", gap: 18 }}>
        <div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {item.badges?.map((b) => (
              <Badge
                key={b}
                tone={
                  b === "Popular" || b === "Chef's pick"
                    ? "primary"
                    : b === "Spicy"
                    ? "danger"
                    : "neutral"
                }
              >
                {b === "Spicy" && <Icons.flame size={10} />}
                {b === "Vegan" || b === "Veg" ? <Icons.leaf size={10} /> : null}
                {b}
              </Badge>
            ))}
          </div>
          <div
            className="display"
            style={{
              fontSize: 24,
              color: "var(--ink-1)",
              lineHeight: 1.15,
            }}
          >
            {item.name}
          </div>
          <div style={{ fontSize: 14, color: "var(--ink-2)", marginTop: 6, lineHeight: 1.5 }}>
            {item.desc}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginTop: 12,
              flexWrap: "wrap",
            }}
          >
            <div className="num" style={{ fontSize: 20, fontWeight: 700 }}><Price amount={effectiveBasePrice} /></div>
            <div style={{ width: 1, height: 14, background: "var(--border)" }} />
            {/* <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 13,
                color: "var(--ink-2)",
              }}
            >
              <Icons.clock size={13} /> {t("v2.item.ready_in_min", { count: 12 })}
            </div> */}
          </div>
        </div>

        {hasNutritionData(nutri) && <NutritionPanel n={nutri} />}

        {variantGroups.map((vg) => (
          <div key={vg.id} ref={(el) => (groupRefs.current[vg.id] = el)}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <div className="display" style={{ fontSize: 15, color: "var(--ink-1)" }}>
                {vg.label}
              </div>
              <Badge tone="danger" style={{ flexShrink: 0 }}>
                {t("v2.item.required")}
              </Badge>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {vg.options.map((opt) => {
                const on = variantSel[vg.id] === opt.id;
                const disabled = !opt.isAvailable;
                return (
                  <div
                    key={opt.id}
                    onClick={() => {
                      if (disabled) return;
                      setVariantSel({ ...variantSel, [vg.id]: opt.id });
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: `1px solid ${on ? "var(--primary-600)" : "var(--border)"}`,
                      background: on ? "var(--primary-50)" : "var(--surface-1)",
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.5 : 1,
                      minHeight: 48,
                    }}
                  >
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 999,
                        border: `2px solid ${on ? "var(--primary-600)" : "var(--border-strong)"}`,
                        background: on ? "var(--primary-600)" : "transparent",
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      {on && (
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            background: "#fff",
                          }}
                        />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          fontSize: 14,
                          fontWeight: 500,
                          color: "var(--ink-1)",
                          lineHeight: 1.2,
                        }}
                      >
                        <span>{opt.label}</span>
                        {disabled && (
                          <Badge tone="danger" style={{ flexShrink: 0 }}>
                            {t("v2.item.sold_out")}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--ink-1)",
                      }}
                    >
                      {opt.price > 0 ? <span style={{display: "inline-flex", alignItems: "center"}}>+<Price amount={opt.price} /></span> : t("v2.item.included")}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {groups.map((g) => {
          const c = g.multi ? multiCount(g.id) : null;
          const min = g.min ?? (g.required ? 1 : 0);
          const max = g.max ?? Infinity;
          const valid = groupValid(g);
          const showError = attempted && !valid;
          const helper = g.multi
            ? min === max
              ? t("v2.item.choose_count", { count: min })
              : max === Infinity
              ? t("v2.item.choose_at_least", { count: min })
              : min === 0
              ? t("v2.item.up_to_max", { max })
              : t("v2.item.choose_range", { min, max })
            : g.required
            ? t("v2.item.choose_one")
            : t("v2.item.optional");
          return (
            <div
              key={g.id}
              ref={(el) => {
                groupRefs.current[g.id] = el;
              }}
              style={{ scrollMarginTop: 80 }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  marginBottom: 10,
                  paddingLeft: showError ? 10 : 0,
                  borderLeft: showError ? "3px solid #c4322a" : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div className="display" style={{ fontSize: 15, color: "var(--ink-1)" }}>
                      {g.label}
                    </div>
                    {g.required && (
                      <Badge tone="danger" style={{ flexShrink: 0 }}>
                        {t("v2.item.required")}
                      </Badge>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: showError ? "#c4322a" : "var(--ink-2)",
                      fontWeight: showError ? 600 : 500,
                    }}
                  >
                    {showError
                      ? g.multi
                        ? min === max
                          ? t("v2.item.please_pick_count", { count: min })
                          : t("v2.item.please_pick_range", { min, max })
                        : t("v2.item.please_pick_one")
                      : helper}
                  </div>
                </div>
                {g.multi && c !== null && (
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      fontFamily: "var(--font-mono, ui-monospace, monospace)",
                      color:
                        c > max
                          ? "#c4322a"
                          : c >= min
                          ? "var(--primary-600)"
                          : "var(--ink-3)",
                      background: "var(--surface-2)",
                      padding: "4px 8px",
                      borderRadius: 999,
                      flexShrink: 0,
                    }}
                  >
                    {c}
                    {max !== Infinity ? `/${max}` : ""}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {g.options.map((opt) => {
                  const v = sel[g.id];
                  const q = g.multi ? optQty(g.id, opt.id) : 0;
                  const on = g.multi ? q > 0 : v === opt.id;
                  const qtyMode = g.multi && optionMax(g, opt) > 1;
                  console.log("g.id:", g.id, "opt.id:", opt.id, "g.multi:", g.multi, "optionMax:", optionMax(g, opt), "opt.maxQuantity:", opt.maxQuantity, "g.max:", g.max);
                  const groupFull = g.multi && multiCount(g.id) >= max;
                  const soldOut = opt.isAvailable === false;

                  return (
                    <div
                      key={opt.id}
                      onClick={() => {
                        if (soldOut) return;
                        g.multi
                          ? qtyMode
                            ? null
                            : toggleMulti(g, opt.id)
                          : setSel({ ...sel, [g.id]: !g.required && on ? null : opt.id });
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 12px",
                        borderRadius: 12,
                        border: `1px solid ${on ? "var(--primary-600)" : "var(--border)"}`,
                        background: on ? "var(--primary-50)" : "var(--surface-1)",
                        cursor: soldOut ? "not-allowed" : qtyMode ? "default" : "pointer",
                        opacity: soldOut ? 0.5 : 1,
                        minHeight: 48,
                      }}
                    >
                      {!qtyMode && (
                        <div
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: g.multi ? 6 : 999,
                            border: `2px solid ${on ? "var(--primary-600)" : "var(--border-strong)"}`,
                            background: on ? "var(--primary-600)" : "transparent",
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                          }}
                        >
                          {on &&
                            (g.multi ? (
                              <Icons.check size={12} stroke="#fff" />
                            ) : (
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: 999,
                                  background: "#fff",
                                }}
                              />
                            ))}
                        </div>
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 14,
                            fontWeight: 500,
                            color: "var(--ink-1)",
                            lineHeight: 1.2,
                          }}
                        >
                          <span>{opt.label}</span>
                          {soldOut && (
                            <Badge tone="danger" style={{ flexShrink: 0 }}>
                              {t("v2.item.sold_out")}
                            </Badge>
                          )}
                        </div>
                        {opt.price > 0 && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--ink-2)",
                              fontWeight: 600,
                              marginTop: 2,
                            }}
                          >
                            +<Price amount={opt.price} />
                            {q > 1 ? ` × ${q}` : ""}
                          </div>
                        )}
                      </div>

                      {qtyMode &&
                        (q === 0 ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              incOpt(g, opt.id);
                            }}
                            disabled={groupFull}
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 999,
                              border: `1px solid ${
                                groupFull ? "var(--border)" : "var(--primary-600)"
                              }`,
                              background: groupFull ? "var(--surface-2)" : "var(--surface-1)",
                              color: groupFull ? "var(--ink-3)" : "var(--primary-600)",
                              fontSize: 18,
                              lineHeight: 1,
                              fontWeight: 600,
                              cursor: groupFull ? "not-allowed" : "pointer",
                              display: "grid",
                              placeItems: "center",
                              flexShrink: 0,
                            }}
                          >
                            +
                          </button>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              background: "var(--primary-100)",
                              borderRadius: 999,
                              padding: 3,
                              flexShrink: 0,
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                decOpt(g, opt.id);
                              }}
                              style={qtyBtn}
                            >
                              {q === 1 ? <Icons.trash size={14} /> : "−"}
                            </button>
                            <span
                              style={{
                                minWidth: 18,
                                textAlign: "center",
                                fontWeight: 700,
                                color: "var(--primary-700)",
                                fontSize: 14,
                                fontFamily: "var(--font-mono, ui-monospace, monospace)",
                              }}
                            >
                              {q}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                incOpt(g, opt.id);
                              }}
                              disabled={q >= optionMax(g, opt) || groupFull}
                              style={{
                                ...qtyBtn,
                                opacity: q >= optionMax(g, opt) || groupFull ? 0.4 : 1,
                              }}
                            >
                              +
                            </button>
                          </div>
                        ))}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        <div>
          {!showNotes ? (
            <button
              onClick={() => setShowNotes(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "12px 14px",
                width: "100%",
                borderRadius: 12,
                border: "1px dashed var(--border-strong)",
                background: "transparent",
                color: "var(--ink-2)",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <span style={{ fontSize: 18, lineHeight: 1, color: "var(--ink-3)" }}>＋</span>
              {t("v2.item.add_note_kitchen")}
            </button>
          ) : (
            <div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "var(--ink-1)",
                  marginBottom: 8,
                }}
              >
                {t("v2.item.special_instructions")}
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("v2.item.notes_placeholder")}
                autoFocus
                style={{
                  width: "100%",
                  minHeight: 76,
                  resize: "vertical",
                  padding: 12,
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  fontSize: 14,
                  fontFamily: "inherit",
                  outline: "none",
                  background: "var(--surface-1)",
                  color: "var(--ink-1)",
                }}
              />
            </div>
          )}
        </div>

        {!!itemReviews.summary && itemReviews.summary.count > 0 && (
          <ItemReviewsSection itemReviews={itemReviews} />
        )}

        {relatedItems.length > 0 && (
          <RelatedItemsRail
            items={relatedItems}
            onOpen={(next) => navigate(`/item/${next.id}`)}
          />
        )}
      </div>

      <div
        style={{
          position: "sticky",
          left: 0,
          right: 0,
          bottom: 0,
          background: "var(--surface-1)",
          borderTop: "1px solid var(--border)",
          padding: "12px 16px calc(env(safe-area-inset-bottom, 0px) + 14px)",
          boxShadow: "0 -4px 16px -8px rgba(0,0,0,0.12)",
          zIndex: 10,
        }}
      >
        {isClosed && (
          <div
            style={{
              fontSize: 13,
              color: "#92400e",
              fontWeight: 600,
              background: "#fef3c7",
              border: "1px solid #fde68a",
              borderRadius: 10,
              padding: "8px 12px",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Icons.info size={14} />
            {opensAt
              ? t("v2.checkout.err_branch_closed_opens", { time: opensAt })
              : t("v2.checkout.err_branch_closed")}
          </div>
        )}
        {!isClosed && soldOut && (
          <div
            style={{
              fontSize: 13,
              color: "#92400e",
              fontWeight: 600,
              background: "#fef3c7",
              border: "1px solid #fde68a",
              borderRadius: 10,
              padding: "8px 12px",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Icons.info size={14} />
            {t("v2.item.currently_unavailable")}
          </div>
        )}
        {!soldOut && attempted && !isValid && (
          <div
            style={{
              fontSize: 12,
              color: "#c4322a",
              fontWeight: 600,
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: 999,
                background: "#c4322a",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              !
            </span>
            {invalidGroups.length === 1
              ? t("v2.item.please_complete_one", { group: invalidGroups[0].label })
              : t("v2.item.please_complete_many", { count: invalidGroups.length })}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Stepper value={qty} onChange={setQty} />
          <Button
            full
            size="lg"
            onClick={handleAdd}
            disabled={orderingDisabled || placing || !isValid}
            style={
              orderingDisabled || !isValid
                ? { background: "var(--ink-3)", boxShadow: "none" }
                : undefined
            }
          >
            {placing ? (
              t("v2.common.loading", "Loading...")
            ) : isClosed && !isMenuOnly ? (
              t("v2.home.closed")
            ) : soldOut ? (
              t("v2.item.sold_out")
            ) : editLine ? (
              t("v2.item.save_changes")
            ) : (
              <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <span>
                  {isMenuOnly
                    ? t("v2.shortlist.add")
                    : appendOrderId
                      ? t("v2.dine_in_order.add_more", "Add to order")
                      : t("v2.item.add_to_cart")}
                </span>
                <Price amount={lineTotal} />
              </div>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ItemBranchSelectionRequired({
  onBack,
  onSelectLocation,
}: {
  onBack: () => void;
  onSelectLocation: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ minHeight: "100%", padding: "14px 16px 24px" }}>
      <button onClick={onBack} aria-label={t("v2.common.back")} style={floatBtn}>
        <Icons.back size={18} />
      </button>
      <Card padding={22} style={{ textAlign: "start", marginTop: 24 }}>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 14,
            background: "var(--primary-50)",
            color: "var(--primary-700)",
            display: "grid",
            placeItems: "center",
            marginBottom: 14,
          }}
        >
          <Icons.pin size={22} />
        </div>
        <div className="display" style={{ fontSize: 18, color: "var(--ink-1)" }}>
          {t("v2.home.select_branch_title")}
        </div>
        <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5, marginTop: 6 }}>
          {t("v2.home.select_branch_desc")}
        </div>
        <Button
          full
          size="lg"
          onClick={onSelectLocation}
          leading={<Icons.pin size={16} />}
          style={{ marginTop: 18 }}
        >
          {t("v2.home.set_delivery_location")}
        </Button>
      </Card>
    </div>
  );
}

function ItemReviewsSection({
  itemReviews,
}: {
  itemReviews: ReturnType<typeof useItemReviews>;
}) {
  const { t } = useTranslation();
  const { summary, reviews, nextPage, loadingMore, loadMore } = itemReviews;
  if (!summary) return null;
  const avgRating = Math.round(summary.avgRating * 10) / 10;

  return (
    <div style={{ padding: "18px 0 0" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div className="display" style={{ fontSize: 16, color: "var(--ink-1)" }}>
          {t("v2.reviews.public.section_title", "Reviews")}
        </div>
        <div
          style={{ display: "flex", alignItems: "center", gap: 6 }}
          aria-label={
            t("v2.reviews.public.rating_aria", "Rated {{rating}} out of 5 from {{count}} reviews", {
              rating: avgRating,
              count: summary.count,
            }) as string
          }
        >
          <StarRating value={Math.round(avgRating)} readOnly size={15} />
          <span className="num" style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink-1)" }}>
            {avgRating}
          </span>
          <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>({summary.count})</span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {reviews.map((r, i) => (
          <PublicReviewCard key={`${r.createdAt ?? i}-${r.reviewerName}`} review={r} />
        ))}
      </div>
      {nextPage != null && (
        <Button
          variant="outline"
          size="md"
          full
          onClick={loadMore}
          disabled={loadingMore}
          style={{ marginTop: 12 }}
        >
          {loadingMore
            ? t("v2.common.loading", "Loading...")
            : t("v2.reviews.public.see_all", "See all")}
        </Button>
      )}
    </div>
  );
}

function RelatedItemsRail({
  items,
  onOpen,
}: {
  items: UiMenuItem[];
  onOpen: (item: UiMenuItem) => void;
}) {
  const { t } = useTranslation();
  const { format } = useFormatPrice();
  return (
    <div style={{ marginInline: -16, padding: "2px 0 0" }}>
      <div style={{ padding: "0 16px", marginBottom: 10 }}>
        <div className="display" style={{ fontSize: 16, color: "var(--ink-1)" }}>
          {t("recommended")}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          gap: 12,
          overflowX: "auto",
          padding: "0 16px 4px",
        }}
      >
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => onOpen(item)}
            style={{
              width: 150,
              flex: "0 0 150px",
              border: "1px solid var(--border)",
              background: "var(--card-bg)",
              borderRadius: "var(--radius-card)",
              boxShadow: "var(--card-shadow)",
              padding: 0,
              overflow: "hidden",
              textAlign: "start",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <FoodImg
              tone={item.tone}
              rounded={0}
              label={item.id}
              src={item.imageUrl}
              alt={item.name}
              style={{ aspectRatio: "1.25 / 1" }}
            />
            <div style={{ padding: 10 }}>
              <div
                className="display"
                style={{
                  fontSize: 13,
                  color: "var(--ink-1)",
                  lineHeight: 1.25,
                  minHeight: 34,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {item.name}
              </div>
              <div className="price num" style={{ fontSize: 13, fontWeight: 800, marginTop: 7 }}>
                <Price amount={item.price} />
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function NutritionPanel({ n }: { n: Nutri }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      style={{
        background: "var(--surface-1)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1,
          background: "var(--border)",
        }}
      >
        <NutCell label={t("v2.item.nutrition.calories")} value={n.calories} unit={t("v2.item.nutrition.kcal")} icon={<Icons.flame size={14} />} />
        <NutCell label={t("v2.item.nutrition.walk_to_burn")} value={n.walkingMin} unit={t("v2.item.nutrition.min")} icon={<WalkIcon />} />
        <NutCell
          label={t("v2.item.nutrition.sodium")}
          value={n.sodium}
          unit={t("v2.item.nutrition.mg")}
          warn={n.sodiumHigh}
          warnLabel={t("v2.item.nutrition.high")}
          icon={<SaltIcon />}
        />
        <NutCell
          label={t("v2.item.nutrition.caffeine")}
          value={n.caffeine || "—"}
          unit={n.caffeine ? t("v2.item.nutrition.mg") : ""}
          icon={<CaffeineIcon />}
        />
      </div>
      <button
        onClick={() => setExpanded((e) => !e)}
        style={{
          width: "100%",
          padding: "10px 14px",
          background: "var(--surface-2)",
          border: "none",
          borderTop: "1px solid var(--border)",
          fontSize: 12,
          fontWeight: 600,
          color: "var(--ink-2)",
          cursor: "pointer",
          fontFamily: "inherit",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              fontSize: 10,
              fontFamily: "var(--font-mono, monospace)",
              color: "var(--ink-3)",
              textTransform: "uppercase",
              letterSpacing: 0.6,
            }}
          >
            SFDA
          </span>
          {expanded ? t("v2.item.nutrition.hide_full") : t("v2.item.nutrition.show_full")}
        </span>
        <span
          style={{
            transform: expanded ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
            fontSize: 14,
          }}
        >
          ▾
        </span>
      </button>
      {expanded && (
        <div
          style={{
            padding: "10px 14px 14px",
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
            background: "var(--surface-2)",
          }}
        >
          <NutMini label={t("v2.item.nutrition.fat")} value={`${n.fat} ${t("v2.item.nutrition.g")}`} />
          <NutMini label={t("v2.item.nutrition.sugar")} value={`${n.sugar} ${t("v2.item.nutrition.g")}`} />
          <NutMini label={t("v2.item.nutrition.protein")} value={`${n.protein} ${t("v2.item.nutrition.g")}`} />
          <div
            style={{
              gridColumn: "1 / -1",
              fontSize: 11,
              color: "var(--ink-3)",
              lineHeight: 1.4,
              marginTop: 4,
            }}
          >
            {t("v2.item.nutrition.values_disclaimer")}
          </div>
        </div>
      )}
    </div>
  );
}

function NutCell({
  label,
  value,
  unit,
  warn,
  warnLabel,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  unit?: string;
  warn?: boolean;
  warnLabel?: string;
  icon: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: "var(--surface-1)",
        padding: "10px 12px",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: warn ? "#fff4e0" : "var(--surface-2)",
          color: warn ? "#d97706" : "var(--ink-2)",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            color: "var(--ink-3)",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            lineHeight: 1.2,
          }}
        >
          {label}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 3, marginTop: 2 }}>
          <span
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: "var(--ink-1)",
              fontFamily: "var(--font-mono, ui-monospace, monospace)",
              letterSpacing: -0.2,
            }}
          >
            {value}
          </span>
          {unit && <span style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 500 }}>{unit}</span>}
          {warn && warnLabel && (
            <span
              style={{
                marginInlineStart: 4,
                fontSize: 9,
                fontWeight: 700,
                color: "#d97706",
                background: "#fff4e0",
                padding: "1px 5px",
                borderRadius: 3,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              {warnLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function NutMini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: "var(--ink-3)",
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: "var(--ink-1)",
          fontFamily: "var(--font-mono, ui-monospace, monospace)",
          marginTop: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function WalkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="13" cy="4" r="2" />
      <path d="M9 21l2-7-3-3 2-5 4 1 3 4 3 1" />
    </svg>
  );
}
function SaltIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M7 8h10l-1 12H8L7 8z" />
      <path d="M9 8V4h6v4" />
      <path d="M11 12v4M13 12v4" />
    </svg>
  );
}
function CaffeineIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 8h13v6a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V8z" />
      <path d="M17 10h2a3 3 0 0 1 0 6h-1" />
      <path d="M8 2c0 2 2 2 2 4M12 2c0 2 2 2 2 4" />
    </svg>
  );
}

function ItemDetailSkeleton() {
  return (
    <div>
      {/* Hero placeholder */}
      <div
        style={{
          width: "100%",
          aspectRatio: "1 / 0.85",
          background: "var(--surface-2)",
        }}
      />
      <div
        style={{
          padding: "18px 16px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div
          style={{
            height: 24,
            width: "60%",
            background: "var(--surface-2)",
            borderRadius: 6,
          }}
        />
        <div
          style={{
            height: 14,
            width: "90%",
            background: "var(--surface-2)",
            borderRadius: 4,
          }}
        />
        <div
          style={{
            height: 14,
            width: "70%",
            background: "var(--surface-2)",
            borderRadius: 4,
          }}
        />
        <div
          style={{
            height: 80,
            width: "100%",
            background: "var(--surface-2)",
            borderRadius: 12,
            marginTop: 12,
          }}
        />
      </div>
    </div>
  );
}

function ItemDetailError({
  onBack,
  onRetry,
}: {
  onBack: () => void;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      <button
        onClick={onBack}
        style={{
          alignSelf: "flex-start",
          width: 38,
          height: 38,
          display: "grid",
          placeItems: "center",
          borderRadius: 999,
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          color: "var(--ink-1)",
          cursor: "pointer",
        }}
        aria-label={t("v2.common.back")}
      >
        <Icons.back size={18} />
      </button>
      <Card padding={28} style={{ textAlign: "center" }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 999,
            background: "var(--surface-2)",
            display: "grid",
            placeItems: "center",
            margin: "0 auto 12px",
            color: "var(--ink-3)",
          }}
        >
          <Icons.info size={22} />
        </div>
        <div style={{ fontWeight: 700, fontSize: 15 }}>{t("v2.item.couldnt_load_menu")}</div>
        <div
          style={{
            fontSize: 13,
            color: "var(--ink-2)",
            marginTop: 4,
            marginBottom: 14,
          }}
        >
          {t("v2.item.couldnt_load_menu_desc")}
        </div>
        <Button onClick={onRetry} variant="primary" size="md">
          {t("v2.common.retry")}
        </Button>
      </Card>
    </div>
  );
}
